import type { CameraAccessListCamera, SiteAccessListSite } from "@visioni/a3s-access/app";

/**
 * Builds the Edit User page's site rows from every source that can name a site:
 * the tenant's sites list, the sites the camera fleet lives at, and the sites the
 * user holds a role on.
 *
 * The sites list alone isn't enough -- it can be empty or incomplete while
 * cameras (and site roles) exist at sites it doesn't return. `AccessListHeader`
 * counts selected sites from the user's roles, so any granted site without a row
 * would show up as "1/0" with nothing checked below it.
 */
export function buildEditUserSiteList(
  listedSites: ReadonlyArray<{ id: string; name: string }>,
  cameras: ReadonlyArray<{ site: { id: string }; snapshot?: { url?: string | null } | null }> | undefined,
  grantedSiteIds: Iterable<string>,
  siteNames: ReadonlyMap<string, string> | undefined,
): SiteAccessListSite[] {
  const snapshotUrls = new Map<string, string | null>();
  for (const camera of cameras ?? []) {
    if (!snapshotUrls.get(camera.site.id)) {
      snapshotUrls.set(camera.site.id, camera.snapshot?.url ?? null);
    }
  }

  const names = new Map(listedSites.map((s) => [s.id, s.name]));
  for (const siteId of [...snapshotUrls.keys(), ...grantedSiteIds]) {
    if (!names.has(siteId)) names.set(siteId, siteNames?.get(siteId) ?? siteId);
  }

  return [...names].map(([id, name]) => ({ id, name, snapshotUrl: snapshotUrls.get(id) ?? null }));
}

/**
 * Ensures every legacy-camera permission a user holds has a corresponding
 * (disabled) row in the camera list, even when the live camera fleet doesn't
 * include it.
 *
 * On web, `useCameras()` filters legacy cameras out of the fleet entirely
 * (they don't support the modern streaming/management APIs), so without this,
 * an existing legacy-camera grant would vanish from `CameraAccessList`'s
 * rendered rows while still counting toward `AccessListHeader`'s "selected"
 * tally -- confusing since the count wouldn't match anything visible. The
 * synthesized row has no name/thumbnail metadata (the API response that would
 * carry it was already filtered before reaching here), so it falls back to
 * the raw camera ID, same as an unnamed live camera falls back to its key.
 */
export function withLegacyCameraFallbacks(
  cameras: CameraAccessListCamera[],
  legacyCameraIds: ReadonlySet<string>,
  siteNames: ReadonlyMap<string, string> | undefined,
): CameraAccessListCamera[] {
  const knownIds = new Set(cameras.map((c) => c.id));
  const missingIds = [...legacyCameraIds].filter((id) => !knownIds.has(id));
  if (missingIds.length === 0) return cameras;

  const fallbacks: CameraAccessListCamera[] = missingIds.map((cameraId) => {
    const siteId = cameraId.split("/")[0] ?? "";
    return {
      id: cameraId,
      name: cameraId,
      siteId,
      siteName: siteNames?.get(siteId) ?? siteId,
      snapshotUrl: null,
      legacy: true,
    };
  });

  return [...cameras, ...fallbacks];
}
